/**
 * Stores > Door times at /stores/door-times.
 *
 * First and last person through the door each day, per store, from the store's
 * people counter, beside that day's posted open and close. A row is flagged
 * "Late open" when nobody came in until 5 or more minutes after opening, and
 * "Closed early" when nobody crossed the door in the last 5 or more minutes
 * before closing (judged only once the day is over). The API decides the flags
 * so a notification job can reuse the same rules; this page only displays them.
 *
 * Filters (store, from, to, only problems) live in the URL so a link survives
 * refresh. Gated by its own stores.doortimes slug. 404/503 -> not-available
 * placeholder.
 */

import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { Pill } from '@/components/Pill';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';
import { denverToday, isNotAvailable, storeLabel, useAskStores } from '@/lib/useStores';
import { useDoorTimes, type DoorTimesRow } from '@/lib/useDoorTimes';
import {
  DOOR_TIMES_MAX_DAYS,
  STATUS_LABEL,
  formatClock12,
  formatDayLabel,
  formatLateBy,
  isProblem,
  lateTone,
  rangeProblem,
  shiftDate,
  statusKinds,
  summarize,
  type StatusKind,
} from '@/lib/doorTimes';
import { StoresHeader, AccessDeniedCard, NotAvailableCard, ErrorCard, inputCls, labelCls } from './storesShared';

const DEFAULT_DAYS = 14;

const LATE_TONE_CLS: Record<'none' | 'amber' | 'red', string> = {
  none: 'text-amp-ink-muted dark:text-amp-dark-ink-muted',
  amber: 'font-medium text-amber-700 dark:text-amber-300',
  red: 'font-semibold text-red-700 dark:text-red-300',
};

const RED_PILL = 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
const GREEN_PILL = 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
const GREY_PILL = 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';

const STATUS_TONE: Record<StatusKind, string> = {
  late_open: RED_PILL,
  closed_early: RED_PILL,
  on_time: GREEN_PILL,
  no_entries: GREY_PILL,
  close_pending: GREY_PILL,
};

export function DoorTimes() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, 'stores.doortimes', user?.superAdmin);

  const [searchParams, setSearchParams] = useSearchParams();
  const today = denverToday();
  const defaultFrom = shiftDate(today, -(DEFAULT_DAYS - 1));
  const from = searchParams.get('from') || defaultFrom;
  const to = searchParams.get('to') || today;
  const storeNo = searchParams.get('store') || '';
  const onlyProblems = searchParams.get('problems') === '1';

  const update = (mutate: (sp: URLSearchParams) => void) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      },
      { replace: true },
    );
  };
  // Defaults stay out of the URL.
  const setFrom = (v: string) => update((sp) => (v && v !== defaultFrom ? sp.set('from', v) : sp.delete('from')));
  const setTo = (v: string) => update((sp) => (v && v !== today ? sp.set('to', v) : sp.delete('to')));
  const setStore = (v: string) => update((sp) => (v ? sp.set('store', v) : sp.delete('store')));
  const setOnlyProblems = (v: boolean) => update((sp) => (v ? sp.set('problems', '1') : sp.delete('problems')));

  const problem = rangeProblem(from, to);
  const storesQ = useAskStores(canView);
  const doorQ = useDoorTimes(from, to, storeNo || null, canView && problem === null);
  const allRows = useMemo(() => doorQ.data?.rows ?? [], [doorQ.data]);
  const rows = useMemo(() => (onlyProblems ? allRows.filter(isProblem) : allRows), [allRows, onlyProblems]);
  const summary = useMemo(() => summarize(allRows), [allRows]);

  if (user && !canView) return <AccessDeniedCard what="Door times" />;

  const header = (
    <StoresHeader
      title="Door times"
      subtitle="When the first person came in and the last person left each day, at any hour, from the store's people counter, next to the first and last register sale. A store is flagged when nobody comes in until 5 or more minutes after opening, or the last person leaves 5 or more minutes before closing."
      actions={<ToolPinToggle toolSlug="stores-door-times" toolName="Door times" />}
    />
  );

  if (isNotAvailable(doorQ.error)) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <NotAvailableCard what="Door times" />
      </div>
    );
  }

  const filterBar = (
    <div className="flex flex-wrap items-end gap-2 sm:gap-3">
      <div className="min-w-0 flex-1 sm:w-64 sm:flex-initial">
        <label htmlFor="door-store" className={labelCls}>
          Store
        </label>
        <select id="door-store" value={storeNo} onChange={(e) => setStore(e.target.value)} className={inputCls}>
          <option value="">All stores</option>
          {(storesQ.data ?? []).map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      <div className="min-w-0 flex-1 sm:flex-initial">
        <label htmlFor="door-from" className={labelCls}>
          From
        </label>
        <input
          id="door-from"
          type="date"
          value={from}
          max={to}
          onChange={(e) => setFrom(e.target.value)}
          className={inputCls}
        />
      </div>
      <div className="min-w-0 flex-1 sm:flex-initial">
        <label htmlFor="door-to" className={labelCls}>
          To
        </label>
        <input
          id="door-to"
          type="date"
          value={to}
          min={from}
          max={today}
          onChange={(e) => setTo(e.target.value)}
          className={inputCls}
        />
      </div>
      <label
        htmlFor="door-problems"
        className="flex items-center gap-2 pb-2 text-sm text-amp-ink dark:text-amp-dark-ink"
      >
        <input
          id="door-problems"
          type="checkbox"
          checked={onlyProblems}
          onChange={(e) => setOnlyProblems(e.target.checked)}
        />
        Only problems
      </label>
      {problem && <p className="pb-2 text-sm text-red-700 dark:text-red-300">{problem}</p>}
    </div>
  );

  const thCls = 'px-3 py-2 text-left font-medium';
  const thRightCls = 'px-3 py-2 text-right font-medium';
  const muted = 'px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted';
  const COLS = 11;

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}

      {doorQ.data && !problem && (
        <p className="mb-3 text-sm font-medium text-amp-ink dark:text-amp-dark-ink" aria-live="polite">
          {summary.lateOpens} late {summary.lateOpens === 1 ? 'open' : 'opens'} and {summary.earlyCloses} early{' '}
          {summary.earlyCloses === 1 ? 'close' : 'closes'} across {summary.storeDays} store-
          {summary.storeDays === 1 ? 'day' : 'days'}
        </p>
      )}

      <DataGridShell total={rows.length} loaded={rows.length} countNoun="days" toolbar={filterBar}>
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <th className={thCls}>Date</th>
              <th className={thCls}>Store</th>
              <th className={thCls}>Status</th>
              <th className={thRightCls}>Posted open</th>
              <th className={thRightCls}>First in</th>
              <th className={thRightCls}>Late by</th>
              <th className={thRightCls}>First sale</th>
              <th className={thRightCls}>Posted close</th>
              <th className={thRightCls}>Last sale</th>
              <th className={thRightCls}>Last out</th>
              <th className={thRightCls}>Closed early by</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {problem ? (
              <tr>
                <td colSpan={COLS} className={muted}>
                  {problem}
                </td>
              </tr>
            ) : doorQ.isLoading ? (
              <tr>
                <td colSpan={COLS} className={muted}>
                  Loading door times...
                </td>
              </tr>
            ) : doorQ.error ? (
              <tr>
                <td colSpan={COLS} className="px-4 py-6">
                  <ErrorCard message={doorQ.error.message} />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={COLS} className={muted}>
                  {onlyProblems ? 'No late opens or early closes in this range.' : 'No stores with a people counter in this range.'}
                </td>
              </tr>
            ) : (
              rows.map((r) => <DoorRow key={`${r.store_no}|${r.date}`} r={r} />)
            )}
          </tbody>
        </table>
      </DataGridShell>
      <p className="mt-2 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Ranges up to {DOOR_TIMES_MAX_DAYS} days. Times are store-local. Early close is judged once the day is over.
      </p>
    </div>
  );
}

function DoorRow({ r }: { r: DoorTimesRow }) {
  const muted = 'text-amp-ink-muted dark:text-amp-dark-ink-muted';
  const ink = 'text-amp-ink dark:text-amp-dark-ink';
  const hasData = r.entries > 0 || r.last_crossing !== null;
  const flagged = isProblem(r);
  return (
    <tr className={flagged ? 'bg-red-50 dark:bg-red-900/20' : undefined}>
      <td className={`px-3 py-2 whitespace-nowrap ${ink}`}>{formatDayLabel(r.date, r.weekday)}</td>
      <td className={`px-3 py-2 ${ink}`}>
        <span className="font-medium">{storeLabel(r.store_no, r.store_name)}</span>
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-wrap gap-1">
          {statusKinds(r).map((k) => (
            <Pill key={k} tone={STATUS_TONE[k]}>
              {STATUS_LABEL[k]}
            </Pill>
          ))}
        </div>
      </td>
      <td className={`px-3 py-2 text-right whitespace-nowrap ${r.posted_open ? ink : muted}`}>
        {formatClock12(r.posted_open)}
      </td>
      <td className={`px-3 py-2 text-right whitespace-nowrap ${r.first_entry ? ink : muted}`}>
        {formatClock12(r.first_entry)}
      </td>
      <td className={`px-3 py-2 text-right whitespace-nowrap ${LATE_TONE_CLS[lateTone(r.minutes_late)]}`}>
        {formatLateBy(r.minutes_late)}
      </td>
      <td className={`px-3 py-2 text-right whitespace-nowrap ${r.first_sale ? ink : muted}`}>
        {formatClock12(r.first_sale ?? null)}
      </td>
      <td className={`px-3 py-2 text-right whitespace-nowrap ${r.posted_close ? ink : muted}`}>
        {formatClock12(r.posted_close)}
      </td>
      <td className={`px-3 py-2 text-right whitespace-nowrap ${r.last_sale ? ink : muted}`}>
        {formatClock12(r.last_sale ?? null)}
      </td>
      <td className={`px-3 py-2 text-right whitespace-nowrap ${hasData ? ink : muted}`}>
        {formatClock12(r.last_exit ?? r.last_crossing)}
      </td>
      <td
        className={`px-3 py-2 text-right whitespace-nowrap ${
          r.closed_early ? 'font-semibold text-red-700 dark:text-red-300' : muted
        }`}
      >
        {r.minutes_early_close === null ? '--' : r.minutes_early_close > 0 ? `${r.minutes_early_close} min` : 'On time'}
      </td>
    </tr>
  );
}
